import { ConsentBoundary } from "#components/module-providers/ConsentBoundary.tsx";
import { MigrationProjectsAccessBoundary } from "#components/module-providers/MigrationProjectsAccessBoundary.tsx";
import { ProjectTenantGate } from "#components/module-providers/ProjectTenantGate.tsx";
import { ProjectTenantProvider } from "#components/module-providers/ProjectTenantProvider.tsx";
import { AssessmentRegionGate } from "#components/module-providers/AssessmentRegionGate.tsx";
import { WorkspaceTenantGate } from "#components/module-providers/WorkspaceTenantGate.tsx";
import { WorkspaceTenantProvider } from "#components/module-providers/WorkspaceTenantProvider.tsx";
import { getPaths, getProjectPaths } from "#paths.ts";
import type { FireflyRuntime, Route } from "@sg-protect/federation-runtime";
import { AuthenticatedLayout, requireFeatureFlagLoader } from "@sg-protect/shell";
import { Outlet, redirect } from "react-router";
import { AssessmentNavbar } from "./components/AssessmentNavbar.tsx";
import { ProjectAssessmentNavbar } from "./features/assessment/ProjectAssessmentNavbar.tsx";

export const registerPublicRoutes = (): Route[] => [];

interface AssessmentRoutePaths {
    migrationProject: string;
    migrationProjectOverview: string;
    migrationProjectIdentities: string;
    migrationProjectOneDrive: string;
    migrationProjectSharePoint: string;
    migrationProjectTeams: string;
}

const assessmentConsentAndRegion = (
    <ConsentBoundary>
        <AssessmentRegionGate>
            <Outlet />
        </AssessmentRegionGate>
    </ConsentBoundary>
);

const workspaceAssessmentBoundaries = (
    <MigrationProjectsAccessBoundary>
        <WorkspaceTenantProvider>
            <WorkspaceTenantGate>{assessmentConsentAndRegion}</WorkspaceTenantGate>
        </WorkspaceTenantProvider>
    </MigrationProjectsAccessBoundary>
);

const projectAssessmentBoundaries = (
    <MigrationProjectsAccessBoundary>
        <ProjectTenantProvider>
            <ProjectTenantGate>{assessmentConsentAndRegion}</ProjectTenantGate>
        </ProjectTenantProvider>
    </MigrationProjectsAccessBoundary>
);

function assessmentTabRoutes(paths: AssessmentRoutePaths): Route[] {
    return [
        {
            index: true,
            loader: ({ request }) => {
                const { search } = new URL(request.url);
                return redirect(`./overview${search}`);
            }
        },
        {
            path: paths.migrationProjectOverview,
            lazy: () => import("#pages/assessment.overview.tsx"),
            handle: { trackingName: "Migration Assessment Overview Tab", pageTitle: "Overview" }
        },
        {
            path: paths.migrationProjectIdentities,
            lazy: () => import("#pages/assessment.identities.tsx"),
            handle: { trackingName: "Migration Assessment Identities Tab", pageTitle: "Identities" }
        },
        {
            path: paths.migrationProjectOneDrive,
            lazy: () => import("#pages/assessment.one-drive.tsx"),
            handle: { trackingName: "Migration Assessment OneDrive Tab", pageTitle: "OneDrive" }
        },
        {
            path: paths.migrationProjectSharePoint,
            lazy: () => import("#pages/assessment.sharepoint.tsx"),
            handle: { trackingName: "Migration Assessment SharePoint Tab", pageTitle: "SharePoint" }
        },
        {
            path: paths.migrationProjectTeams,
            lazy: () => import("#pages/assessment.teams.tsx"),
            handle: { trackingName: "Migration Assessment Teams Tab", pageTitle: "Teams" }
        }
    ];
}

export const registerPrivateRoutes = (runtime: FireflyRuntime): Route[] => {
    const paths = getPaths();
    const projectPaths = getProjectPaths({});

    return [
        {
            element: <AuthenticatedLayout />,
            children: [
                {
                    path: paths.root,
                    lazy: () => import("#pages/index.tsx"),
                    handle: { trackingName: "Migrate Page", pageTitle: "Migrate" }
                },
                {
                    element: workspaceAssessmentBoundaries,
                    children: [
                        {
                            path: paths.migrationProject,
                            lazy: () => import("#pages/assessment.tsx"),
                            handle: { trackingName: "Migration Project Page", pageTitle: "Migration Project" },
                            children: [{ element: <AssessmentNavbar />, children: assessmentTabRoutes(paths) }]
                        }
                    ]
                },
                {
                    element: projectAssessmentBoundaries,
                    path: projectPaths.migrationProject,
                    children: [
                        {
                            lazy: () => import("#pages/assessment.tsx"),
                            handle: { trackingName: "Migration Project Page", pageTitle: "Migration Project" },
                            children: [
                                { element: <ProjectAssessmentNavbar />, children: assessmentTabRoutes(projectPaths) },
                                {
                                    path: projectPaths.migrationProjectPlan,
                                    loader: () => requireFeatureFlagLoader(runtime, "MigrationPlanReleased"),
                                    lazy: () => import("#pages/assessment.plan.tsx"),
                                    handle: { trackingName: "Migration Project Plan Tab", pageTitle: "Plan" }
                                },
                                {
                                    path: projectPaths.migrationProjectActivity,
                                    loader: () => requireFeatureFlagLoader(runtime, "MigrationActivityReleased"),
                                    lazy: () => import("#pages/assessment.activity.tsx"),
                                    handle: { trackingName: "Migration Project Activity Tab", pageTitle: "Activity" }
                                }
                            ]
                        }
                    ]
                }
            ]
        }
    ];
};
